import { createFileRoute } from '@tanstack/react-router';
import { useState, useEffect } from 'react';
import { User, Lock, Eye, EyeOff, CheckCircle2, AlertCircle, BookOpen } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { ErrorCode } from '@signalvote/shared';
import { useAuth } from '@/contexts/AuthContext';
import { authService } from '@/services/authService';
import { API_BASE_URL } from '@/config';
import { validatePassword, getPasswordHelperText } from '@/utils/passwordValidation';
import { CalendarConnectionsCard, CampaignVisibilitySettings } from '@/components/calendar';
import { SettingsScopeSection, SettingsNavRow, ThisCampaignSection, DemoControlsSection, AdminSection } from '@/components/settings';
import { ExpressLinkCard } from '@/components/express';

export const Route = createFileRoute('/app/settings/')({
  component: UserSettingsPage,
});

function FeedbackAlert({ result }: { result: { success?: string; error?: string } | null }) {
  if (!result) return null;
  if (result.success) {
    return (
      <div className="flex items-center gap-2 rounded-md border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-600">
        <CheckCircle2 className="h-4 w-4 shrink-0" />
        <span>{result.success}</span>
      </div>
    );
  }
  if (result.error) {
    return (
      <div className="flex items-center gap-2 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-600">
        <AlertCircle className="h-4 w-4 shrink-0" />
        <span>{result.error}</span>
      </div>
    );
  }
  return null;
}

type AuthUser = ReturnType<typeof useAuth>['user'];

function profileFormFrom(user: AuthUser) {
  return {
    firstName: user?.firstName || '',
    lastName: user?.lastName || '',
    email: user?.email || '',
    phoneNumber: user?.phoneNumber || '',
  };
}

export function UserSettingsPage() {
  const { user, refreshUser } = useAuth();

  const [profileForm, setProfileForm] = useState(() => profileFormFrom(user));
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileResult, setProfileResult] = useState<{ success?: string; error?: string } | null>(null);

  useEffect(() => {
    if (user) setProfileForm(profileFormFrom(user));
  }, [user]);

  const handleProfileSave = async () => {
    if (!profileForm.firstName || !profileForm.lastName || !profileForm.email) {
      setProfileResult({ error: 'First name, last name, and email are required' });
      return;
    }

    setProfileLoading(true);
    setProfileResult(null);

    try {
      const response = await authService.authenticatedFetch(
        `${API_BASE_URL}/api/auth/me`,
        {
          method: 'PUT',
          body: JSON.stringify({
            firstName: profileForm.firstName,
            lastName: profileForm.lastName,
            email: profileForm.email,
            phoneNumber: profileForm.phoneNumber || null,
          }),
        }
      );

      if (response.ok) {
        await authService.refreshUserData();
        refreshUser();
        setProfileResult({ success: 'Profile updated successfully' });
      } else {
        const data = await response.json().catch(() => ({}));
        // The server saved none of it, so the form shows what it kept.
        if (data.code === ErrorCode.EmailAlreadyRegistered) setProfileForm(profileFormFrom(user));
        setProfileResult({ error: data.error || 'Failed to update profile' });
      }
    } catch {
      setProfileResult({ error: 'Failed to update profile' });
    } finally {
      setProfileLoading(false);
    }
  };

  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordChangeLoading, setPasswordChangeLoading] = useState(false);
  const [passwordResult, setPasswordResult] = useState<{ success?: string; error?: string } | null>(null);

  const handlePasswordChange = async () => {
    if (!passwordForm.currentPassword || !passwordForm.newPassword || !passwordForm.confirmPassword) {
      setPasswordResult({ error: 'All fields are required' });
      return;
    }

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setPasswordResult({ error: 'New passwords do not match' });
      return;
    }

    const validation = validatePassword(passwordForm.newPassword);
    if (!validation.isValid) {
      setPasswordResult({ error: validation.errors[0] });
      return;
    }

    setPasswordChangeLoading(true);
    setPasswordResult(null);

    try {
      const response = await authService.authenticatedFetch(
        `${API_BASE_URL}/api/auth/change-password`,
        {
          method: 'POST',
          body: JSON.stringify({
            currentPassword: passwordForm.currentPassword,
            newPassword: passwordForm.newPassword,
          }),
        }
      );

      if (response.ok) {
        setPasswordResult({ success: 'Password changed successfully' });
        setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
      } else {
        const data = await response.json().catch(() => ({}));
        setPasswordResult({ error: data.error || 'Failed to change password' });
      }
    } catch {
      setPasswordResult({ error: 'Failed to change password' });
    } finally {
      setPasswordChangeLoading(false);
    }
  };

  const passwordHelperText = getPasswordHelperText(passwordForm.newPassword);
  const passwordValid = passwordForm.newPassword && validatePassword(passwordForm.newPassword).isValid;
  const confirmMismatch = passwordForm.confirmPassword !== '' && passwordForm.newPassword !== passwordForm.confirmPassword;

  return (
    <div className="max-w-3xl p-6 space-y-8">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Settings</h1>
        <p className="text-sm text-muted-foreground mt-1">Manage your account and this campaign</p>
      </div>

      {/* You — personal settings that follow the user across every campaign */}
      <SettingsScopeSection title="You" description="Settings that follow you across every campaign">
      <SettingsNavRow
        icon={<BookOpen className="h-5 w-5" />}
        label="Your playbook"
        description="Your advice for the campaigns you consult for"
        to="/app/settings/playbook"
      />
      <ExpressLinkCard />
      {/* Personal Information */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg font-semibold">
            <User className="h-5 w-5" />
            Personal Information
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <Separator className="-mt-2" />

          <FeedbackAlert result={profileResult} />

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <label htmlFor="firstName" className="text-sm font-medium">
                First Name
              </label>
              <Input
                id="firstName"
                value={profileForm.firstName}
                onChange={(e) => setProfileForm({ ...profileForm, firstName: e.target.value })}
              />
            </div>
            <div className="space-y-1.5">
              <label htmlFor="lastName" className="text-sm font-medium">
                Last Name
              </label>
              <Input
                id="lastName"
                value={profileForm.lastName}
                onChange={(e) => setProfileForm({ ...profileForm, lastName: e.target.value })}
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <label htmlFor="email" className="text-sm font-medium">
              Email
            </label>
            <Input
              id="email"
              type="email"
              value={profileForm.email}
              onChange={(e) => setProfileForm({ ...profileForm, email: e.target.value })}
            />
          </div>

          <div className="space-y-1.5">
            <label htmlFor="phone" className="text-sm font-medium">
              Phone Number
            </label>
            <Input
              id="phone"
              value={profileForm.phoneNumber}
              onChange={(e) => setProfileForm({ ...profileForm, phoneNumber: e.target.value })}
              placeholder="Optional"
            />
          </div>

          <Button onClick={handleProfileSave} disabled={profileLoading}>
            {profileLoading ? 'Saving...' : 'Save Changes'}
          </Button>
        </CardContent>
      </Card>

      {/* Calendar Integration */}
      <CalendarConnectionsCard />
      <CampaignVisibilitySettings />

      {/* Change Password */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg font-semibold">
            <Lock className="h-5 w-5" />
            Change Password
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <Separator className="-mt-2" />

          <FeedbackAlert result={passwordResult} />

          <div className="space-y-1.5">
            <label htmlFor="currentPassword" className="text-sm font-medium">
              Current Password
            </label>
            <div className="relative">
              <Input
                id="currentPassword"
                type={showCurrentPassword ? 'text' : 'password'}
                value={passwordForm.currentPassword}
                onChange={(e) => setPasswordForm({ ...passwordForm, currentPassword: e.target.value })}
                className="pr-10"
              />
              <button
                type="button"
                onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors duration-200"
              >
                {showCurrentPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>

          <div className="space-y-1.5">
            <label htmlFor="newPassword" className="text-sm font-medium">
              New Password
            </label>
            <div className="relative">
              <Input
                id="newPassword"
                type={showNewPassword ? 'text' : 'password'}
                value={passwordForm.newPassword}
                onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
                className="pr-10"
              />
              <button
                type="button"
                onClick={() => setShowNewPassword(!showNewPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors duration-200"
              >
                {showNewPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            <p className={`text-xs ${passwordValid ? 'text-green-600' : 'text-muted-foreground'}`}>
              {passwordHelperText}
            </p>
          </div>

          <div className="space-y-1.5">
            <label htmlFor="confirmPassword" className="text-sm font-medium">
              Confirm New Password
            </label>
            <div className="relative">
              <Input
                id="confirmPassword"
                type={showConfirmPassword ? 'text' : 'password'}
                value={passwordForm.confirmPassword}
                onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })}
                className={`pr-10 ${confirmMismatch ? 'border-red-500 focus-visible:ring-red-500' : ''}`}
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors duration-200"
              >
                {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            {confirmMismatch && (
              <p className="text-xs text-red-600">Passwords do not match</p>
            )}
          </div>

          <Button onClick={handlePasswordChange} disabled={passwordChangeLoading}>
            {passwordChangeLoading ? 'Changing...' : 'Change Password'}
          </Button>
        </CardContent>
      </Card>
      </SettingsScopeSection>

      {/* This campaign — settings scoped to the currently-selected campaign */}
      <ThisCampaignSection />

      {/* Demo controls — shown to all members of a demo campaign (gated on isDemo) */}
      <DemoControlsSection />

      {/* Admin — platform-ops tools (gated to platform admins) */}
      <AdminSection />
    </div>
  );
}
